/**
 * O desenho da aura de um token (docs/spec/scene-auras.md), em px da cena, a partir de onde o
 * token está (em células) e da aura (na unidade da cena).
 *
 * - Círculo: em volta do token, com o raio do token mais o alcance (o alcance conta da borda).
 * - Cone e semicírculo: saem da borda do token, para onde ele olha (`facing`). A ponta fica onde
 *   a direção cruza a borda quadrada do token: o meio do lado a 0° e 90°, a quina a 45° (como o
 *   cone horizontal e o cone diagonal do original). O semicírculo é o cone de 180°.
 * - Retângulo e imagem: centrados no token, girando com ele.
 * - "Em quadrados": as células cujo centro cai na forma (o círculo em degraus do original).
 */
import type { TokenAura } from './model.js';
import { toCells } from './vision.js';

/** Onde o token está: canto superior esquerdo e tamanho em células, direção em graus. */
export type AuraPlace = { x: number; y: number; size: number; facing: number };

export type AuraDrawing =
  | { kind: 'circle'; cx: number; cy: number; r: number }
  | { kind: 'path'; d: string }
  | { kind: 'cells'; fill: string; outline: string; count: number }
  | { kind: 'image'; x: number; y: number; w: number; h: number; angle: number; cx: number; cy: number };

/** Acima disso, "Em quadrados" desenha a forma lisa (o caminho ficaria grande demais). */
export const AURA_MAX_GRID_CELLS = 20_000;

const EPS = 1e-6;
const r2 = (v: number) => Math.round(v * 100) / 100;

/** A direção na tela (y para baixo) de um ângulo a partir do norte, no sentido do relógio. */
const dir = (deg: number) => {
  const rad = (deg * Math.PI) / 180;
  return { x: Math.sin(rad), y: -Math.cos(rad) };
};

/** Gira (dx, dy) por `deg` graus no sentido do relógio da tela. */
const turn = (dx: number, dy: number, deg: number) => {
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return { x: dx * cos - dy * sin, y: dx * sin + dy * cos };
};

const centerOf = (p: AuraPlace) => ({ x: p.x + p.size / 2, y: p.y + p.size / 2 });

/** De onde sai o cone (e o semicírculo): a borda do token, na direção dele, em células. */
export function auraTip(p: AuraPlace): { x: number; y: number } {
  const c = centerOf(p);
  const d = dir(p.facing);
  const k = p.size / 2 / Math.max(Math.abs(d.x), Math.abs(d.y));
  return { x: c.x + d.x * k, y: c.y + d.y * k };
}

/** O setor do cone (ou do semicírculo), em células. */
function sectorOf(p: AuraPlace, aura: TokenAura, cellDistance: number) {
  return { tip: auraTip(p), length: toCells(aura.reach, cellDistance), half: (aura.shape === 'semicircle' ? 180 : aura.angle) / 2 };
}

/** O ponto (em células) está dentro da forma? (círculo, cone ou semicírculo) */
function inside(p: AuraPlace, aura: TokenAura, cellDistance: number): (x: number, y: number) => boolean {
  if (aura.shape === 'circle') {
    const c = centerOf(p);
    const r = p.size / 2 + toCells(aura.reach, cellDistance);
    return (x, y) => (x - c.x) ** 2 + (y - c.y) ** 2 <= r * r + EPS;
  }
  const { tip, length, half } = sectorOf(p, aura, cellDistance);
  const d = dir(p.facing);
  const cosHalf = Math.cos((half * Math.PI) / 180);
  return (x, y) => {
    const vx = x - tip.x;
    const vy = y - tip.y;
    const len = Math.hypot(vx, vy);
    if (len > length + EPS) return false;
    if (len < EPS) return true;
    return (vx * d.x + vy * d.y) / len >= cosHalf - EPS;
  };
}

/**
 * As células (coluna, linha) cujo centro cai na aura, para "Em quadrados" (círculo, cone e
 * semicírculo). Null quando seriam células demais.
 */
export function auraCells(p: AuraPlace, aura: TokenAura, cellDistance: number): [number, number][] | null {
  let box: { x0: number; y0: number; x1: number; y1: number };
  if (aura.shape === 'circle') {
    const c = centerOf(p);
    const r = p.size / 2 + toCells(aura.reach, cellDistance);
    box = { x0: c.x - r, y0: c.y - r, x1: c.x + r, y1: c.y + r };
  } else if (aura.shape === 'cone' || aura.shape === 'semicircle') {
    const { tip, length } = sectorOf(p, aura, cellDistance);
    box = { x0: tip.x - length, y0: tip.y - length, x1: tip.x + length, y1: tip.y + length };
  } else return null;
  const i0 = Math.floor(box.x0);
  const j0 = Math.floor(box.y0);
  const i1 = Math.ceil(box.x1);
  const j1 = Math.ceil(box.y1);
  if ((i1 - i0) * (j1 - j0) > AURA_MAX_GRID_CELLS) return null;
  const test = inside(p, aura, cellDistance);
  const cells: [number, number][] = [];
  for (let j = j0; j < j1; j += 1) for (let i = i0; i < i1; i += 1) if (test(i + 0.5, j + 0.5)) cells.push([i, j]);
  return cells;
}

/** As células como um caminho só (preenchimento) e o contorno só pelas bordas de fora. */
export function cellsPaths(cells: readonly [number, number][], g: number): { fill: string; outline: string } {
  const set = new Set(cells.map(([i, j]) => `${i},${j}`));
  const has = (i: number, j: number) => set.has(`${i},${j}`);
  let fill = '';
  let outline = '';
  for (const [i, j] of cells) {
    const x = r2(i * g);
    const y = r2(j * g);
    const x2 = r2((i + 1) * g);
    const y2 = r2((j + 1) * g);
    fill += `M${x} ${y}H${x2}V${y2}H${x}Z`;
    if (!has(i, j - 1)) outline += `M${x} ${y}H${x2}`;
    if (!has(i + 1, j)) outline += `M${x2} ${y}V${y2}`;
    if (!has(i, j + 1)) outline += `M${x} ${y2}H${x2}`;
    if (!has(i - 1, j)) outline += `M${x} ${y}V${y2}`;
  }
  return { fill, outline };
}

/** O desenho da aura, em px (`g` px por célula). Null quando não há o que desenhar. */
export function auraDrawing(p: AuraPlace, aura: TokenAura, cellDistance: number, g: number): AuraDrawing | null {
  const c = centerOf(p);
  if (aura.shape === 'rect' || aura.shape === 'image') {
    const w = toCells(aura.w, cellDistance);
    const h = toCells(aura.h, cellDistance);
    if (aura.shape === 'image') return { kind: 'image', x: r2((c.x - w / 2) * g), y: r2((c.y - h / 2) * g), w: r2(w * g), h: r2(h * g), angle: p.facing, cx: r2(c.x * g), cy: r2(c.y * g) };
    const corners = [
      [-w / 2, -h / 2],
      [w / 2, -h / 2],
      [w / 2, h / 2],
      [-w / 2, h / 2],
    ].map(([dx, dy]) => turn(dx!, dy!, p.facing));
    return { kind: 'path', d: `${corners.map((k, i) => `${i ? 'L' : 'M'}${r2((c.x + k.x) * g)} ${r2((c.y + k.y) * g)}`).join('')}Z` };
  }
  if (aura.grid) {
    const cells = auraCells(p, aura, cellDistance);
    if (cells) return { kind: 'cells', ...cellsPaths(cells, g), count: cells.length };
  }
  if (aura.shape === 'circle') return { kind: 'circle', cx: r2(c.x * g), cy: r2(c.y * g), r: r2((p.size / 2 + toCells(aura.reach, cellDistance)) * g) };
  const { tip, length, half } = sectorOf(p, aura, cellDistance);
  if (length <= 0) return null;
  const a = dir(p.facing - half);
  const b = dir(p.facing + half);
  const L = length * g;
  const tx = tip.x * g;
  const ty = tip.y * g;
  return {
    kind: 'path',
    d: `M${r2(tx)} ${r2(ty)}L${r2(tx + a.x * L)} ${r2(ty + a.y * L)}A${r2(L)} ${r2(L)} 0 0 1 ${r2(tx + b.x * L)} ${r2(ty + b.y * L)}Z`,
  };
}
